import { normalizeWebsiteUrl } from '@kanbu/schema/shared';

export { normalizeWebsiteUrl } from '@kanbu/schema/shared';

/**
 * Extract canonical host identity for website comparisons.
 * Uses normalizeWebsiteUrl so values like `www.example.com` and `example.com`
 * are treated as the same site.
 */
export function getNormalizedWebsiteHost(url: string): string {
  const normalizedUrl = normalizeWebsiteUrl(url);

  return new URL(normalizedUrl).hostname.toLowerCase();
}

/**
 * Path scope for a website root URL, without trailing slash.
 * `https://example.com/` → ``
 * `https://example.com/blog` → `/blog`
 * `https://example.com/blog/` → `/blog`
 */
export function getWebsiteUrlPathScope(url: string): string {
  const pathname = new URL(normalizeWebsiteUrl(url)).pathname.replace(
    /\/+$/,
    '',
  );

  return pathname === '' || pathname === '/' ? '' : pathname;
}

/**
 * Normalize a user-entered path prefix to `/segment/...` or `` for whole site.
 */
export function normalizeWebsitePathScope(pathPrefix: string): string {
  const trimmed = pathPrefix.trim();

  if (!trimmed || trimmed === '/') {
    return '';
  }

  const withLeadingSlash = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;

  return withLeadingSlash.replace(/\/+$/, '') || '';
}

/**
 * Whether a page URL falls under a path-scoped website root.
 * Empty `pathScope` matches the whole host.
 */
export function urlMatchesPathScope(url: string, pathScope: string): boolean {
  if (!pathScope) {
    return true;
  }

  try {
    const pathname = new URL(normalizeWebsiteUrl(url)).pathname.replace(
      /\/+$/,
      '',
    );

    return pathname === pathScope || pathname.startsWith(`${pathScope}/`);
  } catch {
    return false;
  }
}

/**
 * Two path scopes overlap when one is an ancestor of the other (or equal),
 * including whole-host `` which overlaps every scope.
 */
export function pathScopesOverlap(a: string, b: string): boolean {
  if (a === b) {
    return true;
  }

  if (!a || !b) {
    return true;
  }

  return a.startsWith(`${b}/`) || b.startsWith(`${a}/`);
}

/**
 * Among matching scopes for a URL, the longest path wins
 * (e.g. `/blog` beats `` for `https://host/blog/post?x=1`).
 */
export function findMostSpecificPathScope(
  url: string,
  scopes: string[],
): string | null {
  const matching = scopes.filter(scope => urlMatchesPathScope(url, scope));

  if (matching.length === 0) {
    return null;
  }

  return matching.reduce((best, scope) =>
    scope.length > best.length ? scope : best,
  );
}

/**
 * Whether `candidateScope` is the most specific owner of `url` among `allScopes`.
 */
export function isMostSpecificPathScopeOwner(options: {
  url: string;
  candidateScope: string;
  allScopes: string[];
}): boolean {
  const { url, candidateScope, allScopes } = options;
  const owner = findMostSpecificPathScope(url, allScopes);

  return owner !== null && owner === candidateScope;
}

/**
 * Existing roots on the same host whose path scope overlaps `candidateUrl`
 * (ancestor/descendant), excluding an exact same host+path match.
 */
export function findOverlappingWebsiteRootUrls(options: {
  candidateUrl: string;
  existingRootUrls: string[];
}): string[] {
  const { candidateUrl, existingRootUrls } = options;
  const host = getNormalizedWebsiteHost(candidateUrl);
  const scope = getWebsiteUrlPathScope(candidateUrl);

  return existingRootUrls.filter(rootUrl => {
    try {
      if (getNormalizedWebsiteHost(rootUrl) !== host) {
        return false;
      }

      const otherScope = getWebsiteUrlPathScope(rootUrl);

      if (otherScope === scope) {
        return false;
      }

      return pathScopesOverlap(scope, otherScope);
    } catch {
      return false;
    }
  });
}

/**
 * Rewrite a page URL so path classification is relative to a website root URL.
 * `https://host/blog/2024/post` under root `https://host/blog` → `https://host/2024/post`
 * Whole-host roots are returned normalized unchanged.
 */
export function toWebsiteScopeRelativeUrl(
  url: string,
  websiteRootUrl: string,
): string {
  const pathScope = getWebsiteUrlPathScope(websiteRootUrl);
  const host = getNormalizedWebsiteHost(websiteRootUrl);
  const normalizedUrl = normalizeWebsiteUrl(url);

  if (!pathScope) {
    return normalizedUrl;
  }

  const parsed = new URL(normalizedUrl);
  let pathname = parsed.pathname.replace(/\/+$/, '') || '/';

  if (pathname === pathScope) {
    pathname = '/';
  } else if (pathname.startsWith(`${pathScope}/`)) {
    pathname = pathname.slice(pathScope.length) || '/';
  }

  return normalizeWebsiteUrl(
    `https://${host}${pathname === '/' ? '' : pathname}`,
  );
}

/**
 * Longest common path prefix shared by all URLs (same host assumed).
 * Returns `` when there is no shared directory beyond `/`.
 */
export function getCommonWebsitePathScope(urls: string[]): string {
  if (urls.length === 0) {
    return '';
  }

  const segmentLists = urls.map(url => parseUrlToPathSegments(url));
  const [first, ...rest] = segmentLists;

  if (!first || first.length === 0) {
    return '';
  }

  const common: string[] = [];

  for (const [index, segment] of first.entries()) {
    if (rest.every(segments => segments[index] === segment)) {
      common.push(segment);
    } else {
      break;
    }
  }

  // If every URL is exactly the common path (a single page), use parent scope
  // so promoting one page under /blog/post doesn't create a root at /blog/post.
  if (
    common.length > 0 &&
    segmentLists.every(segments => segments.length === common.length)
  ) {
    common.pop();
  }

  return common.length > 0 ? `/${common.join('/')}` : '';
}

/**
 * Parse a URL into its path segments.
 * Input: 'https://example.com/blog/2024/post-1'
 * Output: ['blog', '2024', 'post-1']
 */
export function parseUrlToPathSegments(url: string): string[] {
  const parsed = new URL(normalizeWebsiteUrl(url));
  const path = parsed.pathname;

  if (path === '/' || path === '') {
    return [];
  }

  return path.split('/').filter(segment => segment.length > 0);
}

export interface UrlClassification {
  /** Segment keys (e.g. 'blog/2024') that are directories (prefix of other URLs). */
  directories: Set<string>;
  /** For each URL: the segment key for the folder it should be placed in. */
  folderKey: Map<string, string>;
  /** URLs that are both pages and directories — should become indexDocument. */
  directoryUrls: Set<string>;
}

/**
 * Classify a list of URLs into directories (folders) vs leaf pages.
 *
 * A URL is a "directory" if its path is a strict prefix of at least one other URL.
 * Leaf pages are placed in their parent directory's folder.
 * Directory URLs are placed in their own folder (as indexDocument).
 */
export function classifyUrlPaths(urls: string[]): UrlClassification {
  const segmentsMap = new Map<string, string[]>();
  const directories = new Set<string>();

  for (const url of urls) {
    const segments = parseUrlToPathSegments(url);
    segmentsMap.set(url, segments);
  }

  // A path is a directory if it's a strict prefix of any other path
  for (const [, segments] of segmentsMap) {
    for (let i = 1; i < segments.length; i++) {
      directories.add(segments.slice(0, i).join('/'));
    }
  }

  const folderKey = new Map<string, string>();
  const directoryUrls = new Set<string>();

  for (const [url, segments] of segmentsMap) {
    const fullKey = segments.join('/');

    if (directories.has(fullKey)) {
      // This URL is also a directory — goes into its own folder as indexDocument
      folderKey.set(url, fullKey);
      directoryUrls.add(url);
    } else {
      // Leaf page — goes into parent directory's folder
      const parentKey =
        segments.length > 1 ? segments.slice(0, -1).join('/') : '';
      folderKey.set(url, parentKey);
    }
  }

  return { directories, folderKey, directoryUrls };
}
